import { useCallback } from "react";
import {
  useLikePostMutation,
  useCommentOnPostMutation,
  useSharePostMutation,
  useUpdatePostMutation,
  useDeletePostMutation,
  useSavePostMutation,
  useUnsavePostMutation,
  useUnlikePostMutation,
} from "../store/api/postApi";
import { toast } from "react-toastify";
/**
 * Custom hook that encapsulates all post operations with error handling
 * @param {string} postId - The ID of the post
 * @param {string} postType - The type of the post
 * @param {Object} callbacks - Optional callbacks for operations
 */
export const usePostOperations = (postId, postType, callbacks = {}) => {
  const { onSuccess, onError } = callbacks;

  const [likePost, { isLoading: isLiking }] = useLikePostMutation();
  const [unlikePost, { isLoading: isUnliking }] = useUnlikePostMutation();
  const [commentOnPost, { isLoading: isCommenting }] =
    useCommentOnPostMutation();
  const [sharePost, { isLoading: isSharing }] = useSharePostMutation();
  const [updatePost, { isLoading: isUpdating }] = useUpdatePostMutation();
  const [deletePost, { isLoading: isDeleting }] = useDeletePostMutation();
  const [savePost, { isLoading: isSaving }] = useSavePostMutation();
  const [unsavePost, { isLoading: isUnsaving }] = useUnsavePostMutation();

  const handleError = useCallback(
    (error, action) => {
      console.error(`${action} error:`, error);

      const errorMessages = {
        401: `Please log in to ${action.toLowerCase()}.`,
        403: `You don't have permission to ${action.toLowerCase()}.`,
        404: `Post not found. It may have been deleted.`,
      };

      const message =
        errorMessages[error?.status] ||
        `Failed to ${action.toLowerCase()}. Please try again.`;

      if (onError) {
        onError(error, message, action);
      } else {
        toast.error(message);
      }
    },
    [onError],
  );

  const handleToggleLike = useCallback(
    async (isLiked) => {
      if (isLiking || isUnliking || !postId || !postType) return false;

      try {
        if (isLiked) {
          await unlikePost({ postId, postType }).unwrap();
          onSuccess?.("unlike");
        } else {
          await likePost({ postId, postType });
          onSuccess?.("like");
        }
        return true;
      } catch (error) {
        handleError(error, isLiked ? "Unlike post" : "Like post");
        return false;
      }
    },
    [
      isLiking,
      isUnliking,
      postId,
      postType,
      likePost,
      unlikePost,
      onSuccess,
      handleError,
    ],
  );

  const handleComment = useCallback(
    async (commentText, parentCommentId = null) => {
      if (isCommenting || !postId || !postType || !commentText?.trim())
        return null;
      try {
        const result = await commentOnPost({
          postId,
          commentText: commentText.trim(),
          parentCommentId,
          type: postType,
        }).unwrap();

        onSuccess?.("comment", result?.comment);
        return result?.comment || true;
      } catch (error) {
        handleError(error, "Post comment");
        return null;
      }
    },
    [isCommenting, postId, postType, commentOnPost, onSuccess, handleError],
  );

  const handleShare = useCallback(async () => {
    if (isSharing || !postId) return false;

    try {
      await sharePost(postId).unwrap();
      onSuccess?.("share");
      return true;
    } catch (error) {
      handleError(error, "Share post");
      return false;
    }
  }, [isSharing, postId, sharePost, onSuccess, handleError]);

  const handleUpdate = useCallback(
    async (content, visibility) => {
      if (isUpdating || !postId || !content?.trim()) return false;

      try {
        await updatePost({
          postId,
          postType,
          caption: content.trim(),
          visibility, // 👈 NEW
        }).unwrap();

        onSuccess?.("update", {
          caption: content.trim(),
          visibility,
        });

        return true;
      } catch (error) {
        handleError(error, "Update post");
        return false;
      }
    },
    [isUpdating, postId, postType, updatePost, onSuccess, handleError],
  );

  const handleDelete = useCallback(async () => {
    if (isDeleting || !postId) return false;

    const confirmed = window.confirm(
      "Are you sure you want to delete this post? This action cannot be undone.",
    );

    if (!confirmed) return false;

    try {
      await deletePost({ postId, postType }).unwrap();
      onSuccess?.("delete");
      return true;
    } catch (error) {
      // If post is already deleted (404), still consider it a success
      if (error?.status === 404) {
        onSuccess?.("delete");
        return true;
      }
      handleError(error, "Delete post");
      return false;
    }
  }, [isDeleting, postId, postType, deletePost, onSuccess, handleError]);

  const handleSave = useCallback(
    async (isSaved) => {
      console.log(
        "postId:",
        postId,
        "postType:",
        postType,
        "isSaved:",
        isSaved,
      );

      if (isSaving || isUnsaving || !postId || !postType) return false;

      console.log(
        "postId:",
        postId,
        "postType:",
        postType,
        "isSaved:",
        isSaved,
      );
      const action = isSaved ? unsavePost : savePost;

      try {
        await action({ postId, type: postType }).unwrap();
        onSuccess?.(isSaved ? "unsave" : "save");
        return true;
      } catch (error) {
        handleError(error, isSaved ? "Unsave post" : "Save post");
        return false;
      }
    },
    [
      isSaving,
      isUnsaving,
      postType,
      postId,
      savePost,
      unsavePost,
      onSuccess,
      handleError,
    ],
  );

  return {
    // Operations
    handleToggleLike,
    handleComment,
    handleShare,
    handleUpdate,
    handleDelete,
    handleSave,

    // Loading states
    isLiking: isLiking || isUnliking,
    isCommenting,
    isSharing,
    isUpdating,
    isDeleting,
    isSaving: isSaving || isUnsaving,

    // Combined loading state
    isPerformingAction:
      isLiking ||
      isUnliking ||
      isCommenting ||
      isSharing ||
      isUpdating ||
      isDeleting ||
      isSaving ||
      isUnsaving,
  };
};
